import React, { useState, useRef, useEffect } from 'react';
import ChatMarkdownRenderer from './ChatMarkdownRenderer';

// Add CSS animations
const chatAnimationStyles = `
  @keyframes cornerExpand {
    0% { 
      transform: scale(0) translateX(25%) translateY(25%);
      transform-origin: bottom right;
      opacity: 0;
    }
    100% { 
      transform: scale(1) translateX(0) translateY(0);
      transform-origin: bottom right;
      opacity: 1;
    }
  }
  
  @keyframes slideUpMobile {
    0% { 
      transform: translateY(100%);
      opacity: 0;
    }
    100% { 
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes buttonPulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
`;

// Inject styles into Shadow DOM (will be handled by CSS bundle)
// Note: These styles are now included in the main CSS bundle loaded into Shadow DOM

/**
 * AgentforceChat - Simplified Salesforce Agentforce Chat Widget
 * 
 * A clean, reusable React component for Salesforce Agentforce integration.
 * Designed for Opptymizer clients with consistent branding.
 * 
 * @author Opptymizer
 * @version 1.0.0
 */

// ===== TYPES =====

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface AgentforceChatProps {
  // Basic Configuration
  orgId: string; // Required: Organization ID for multi-client support
  apiEndpoint?: string;
  companyName?: string;
  welcomeMessage?: string;
  placeholderText?: string;
  widgetDomain?: string; // Domain where widget assets are hosted
  
  // Styling
  primaryColor?: string;
  backgroundColor?: string;
  bubbleColor?: string;
  iconImage?: string; // Chat button icon
  headerImage?: string; // Header icon
  
  // Positioning
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  
  // Callbacks
  onSessionStart?: (sessionId: string) => void;
  onMessageSent?: (message: string) => void;
  onError?: (error: string) => void;
}

// ===== BRAND COLORS =====
// Note: BRAND_COLORS is now created dynamically inside the component using props

// ===== MAIN COMPONENT =====

const AgentforceChat: React.FC<AgentforceChatProps> = ({
  orgId, // Required: Organization ID for multi-client support
  apiEndpoint = '/api/chat',
  companyName = 'Support',
  welcomeMessage = 'Hi! How can I help you today?',
  placeholderText = 'Type your message...',
  widgetDomain = 'https://opptymizer.com',
  primaryColor = '#2060e0',
  backgroundColor = '#f8fafc',
  bubbleColor = '#ffffff',
  iconImage, // Chat button icon
  headerImage, // Header icon
  position = 'bottom-right',
  onSessionStart,
  onMessageSent,
  onError
}) => {
  
  // ===== DYNAMIC BRAND COLORS =====
  const BRAND_COLORS = {
    primary: primaryColor,
    primaryHover: primaryColor.replace('#', '').length === 6 ? 
      `#${Math.max(0, parseInt(primaryColor.replace('#', ''), 16) - 0x111111).toString(16).padStart(6, '0')}` : 
      '#0088cc',
    paleBlueTint: '#f0f4ff',
    secondary: bubbleColor,
    text: '#1e293b',
    textLight: '#64748b',
    white: backgroundColor,
    border: '#e2e8f0'
  };
  
  // ===== STATE =====
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sequenceId, setSequenceId] = useState(1);
  
  // ===== REFS =====
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // ===== UTILITIES =====
  
  const generateMessageId = (): string => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };
  
  const scrollToBottom = () => {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }, 100);
  };
  
  const getPositionClasses = (): string => {
    const baseClasses = 'fixed z-50';
    
    if (isOpen) {
      // When chat is open: Mobile docks at bottom, Desktop positioned in corners
      const mobileClasses = 'bottom-0 left-0 right-0 md:bottom-4 md:left-auto md:right-4 md:top-auto';
      
      switch (position) {
        case 'bottom-right': return `${baseClasses} ${mobileClasses}`;
        case 'bottom-left': return `${baseClasses} bottom-0 left-0 right-0 md:bottom-4 md:left-4 md:right-auto md:top-auto`;
        case 'top-right': return `${baseClasses} bottom-0 left-0 right-0 md:top-4 md:right-4 md:bottom-auto md:left-auto`;
        case 'top-left': return `${baseClasses} bottom-0 left-0 right-0 md:top-4 md:left-4 md:bottom-auto md:right-auto`;
        default: return `${baseClasses} ${mobileClasses}`;
      }
    } else {
      // When chat is closed: Button positioned properly on both mobile and desktop
      switch (position) {
        case 'bottom-right': return `${baseClasses} bottom-4 right-4`;
        case 'bottom-left': return `${baseClasses} bottom-4 left-4`;
        case 'top-right': return `${baseClasses} top-4 right-4`;
        case 'top-left': return `${baseClasses} top-4 left-4`;
        default: return `${baseClasses} bottom-4 right-4`;
      }
    }
  };
  
  // ===== EFFECTS =====
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMsg: ChatMessage = {
        id: generateMessageId(),
        text: welcomeMessage,
        isUser: false,
        timestamp: new Date()
      };
      setMessages([welcomeMsg]);
    }
  }, [isOpen, welcomeMessage]);
  
  // ===== CHAT FUNCTIONS =====
  
  const sendMessage = async () => {
    if (!message.trim() || isLoading) return;
    
    // Add user message
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      text: message.trim(),
      isUser: true,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const currentMessage = message.trim();
    setMessage('');
    setIsLoading(true);
    
    // Scroll to bottom after user message
    scrollToBottom();
    
    // Trigger callback
    onMessageSent?.(currentMessage);
    
    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: currentMessage,
          sessionId: sessionId,
          sequenceId: sequenceId,
          orgId: orgId
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Update session info
        if (data.sessionId && !sessionId) {
          setSessionId(data.sessionId);
          onSessionStart?.(data.sessionId);
        }
        if (data.sequenceId) {
          setSequenceId(data.sequenceId);
        }
        
        // Add bot message
        const botMessage: ChatMessage = {
          id: generateMessageId(),
          text: data.response || 'Thank you for your message!',
          isUser: false,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, botMessage]);
        
        // Scroll to bottom after bot message
        scrollToBottom();
        
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
      
    } catch (error) {
      console.error('[AgentforceChat] Error:', error);
      
      const errorMessage: ChatMessage = {
        id: generateMessageId(),
        text: 'Sorry, I encountered an error. Please try again.',
        isUser: false,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      
      // Scroll to bottom after error message
      scrollToBottom();
      
      onError?.(error instanceof Error ? error.message : 'Unknown error');
      
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };
  
  // ===== RENDER =====
  
  return (
    <div className={getPositionClasses()}>
      {/* Chat Window */}
      {isOpen && (
        <div 
          className="flex flex-col relative overflow-hidden w-full h-[80vh] max-h-[90vh] md:mb-4 md:w-[350px] md:h-[570px] md:max-h-[90vh]"
          style={{ 
            borderRadius: window.innerWidth >= 768 ? '24px' : '24px 24px 0 0',
            '--tw-shadow': '0 25px 50px -12px rgb(0 0 0 / .25)',
            '--tw-shadow-colored': '0 25px 50px -12px var(--tw-shadow-color)',
            boxShadow: 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)',
            animation: 'cornerExpand 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
          } as React.CSSProperties}
        >
          {/* Windows Vista Aero Glass Header */}
          <div 
            className="px-5 py-3 font-semibold flex justify-between items-center backdrop-blur-sm z-10"
            style={{ 
              height: '55px',
              background: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              color: BRAND_COLORS.text,
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <div className="flex items-center space-x-3">
              <img 
                src={headerImage || `${widgetDomain}/agentforce/agentforce_icon.png`} 
                alt="Agentforce" 
                className="w-6 h-6 rounded-full"
              />
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">{companyName}</span>
            </div>
            <button
              onClick={toggleChat}
              className="text-gray-700 hover:text-gray-900 text-xl font-bold w-6 h-6 flex items-center justify-center transition-colors"
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Solid Messages Area */}
          <div 
            className="flex-1 overflow-y-auto p-4 space-y-3" 
            style={{ 
              backgroundColor: BRAND_COLORS.secondary
            }}
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs sm:max-w-sm md:max-w-md px-3 py-2 rounded-2xl shadow-sm ${
                    msg.isUser
                      ? 'text-white'
                      : 'text-gray-800'
                  }`}
                  style={{
                    backgroundColor: msg.isUser ? BRAND_COLORS.primary : BRAND_COLORS.white,
                    color: msg.isUser ? BRAND_COLORS.white : BRAND_COLORS.text
                  }}
                >
                  <ChatMarkdownRenderer 
                    content={msg.text} 
                    isUser={msg.isUser}
                  />
                </div>
              </div>
            ))}
            
            {/* Loading Indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div 
                  className="px-4 py-2 rounded-2xl shadow-sm"
                  style={{ backgroundColor: BRAND_COLORS.white }}
                >
                  <div className="flex space-x-1">
                    <div 
                      className="w-2 h-2 rounded-full animate-bounce"
                      style={{ backgroundColor: BRAND_COLORS.primary }}
                    ></div>
                    <div 
                      className="w-2 h-2 rounded-full animate-bounce"
                      style={{ 
                        backgroundColor: BRAND_COLORS.primary,
                        animationDelay: '0.1s' 
                      }}
                    ></div>
                    <div 
                      className="w-2 h-2 rounded-full animate-bounce"
                      style={{ 
                        backgroundColor: BRAND_COLORS.primary,
                        animationDelay: '0.2s' 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Solid Input Area */}
          <div 
            className="p-5" 
            style={{ 
              backgroundColor: BRAND_COLORS.white,
              borderTop: '1px solid rgba(200, 200, 200, 0.3)'
            }}
          >
            <div className="relative">
              <textarea
                ref={inputRef as any}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={placeholderText}
                disabled={isLoading}
                rows={2}
                className="w-full px-4 py-3 pr-14 border border-gray-200 focus:outline-none focus:ring-2 text-sm resize-none"
                style={{ 
                  borderColor: BRAND_COLORS.border,
                  backgroundColor: BRAND_COLORS.white,
                  minHeight: '60px',
                  borderRadius: '20px'
                  // focusRingColor is not a valid style property
                  // Use ring color via Tailwind classes instead
                }}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !message.trim()}
                className="absolute bottom-3.5 right-2 w-9 h-9 rounded-full flex items-center justify-center text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.primaryHover} 100%)`,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && message.trim()) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <svg 
                  className="w-4 h-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 19V5m-7 7l7-7 7 7" 
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Chat Toggle Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="w-14 h-14 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
          style={{ 
            backgroundColor: BRAND_COLORS.primary,
            animation: 'buttonPulse 2s ease-in-out infinite'
          }}
          aria-label="Open chat"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = BRAND_COLORS.primaryHover;
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.animation = 'none';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = BRAND_COLORS.primary;
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.animation = 'buttonPulse 2s ease-in-out infinite';
          }}
        >
          <img 
            src={iconImage || `${widgetDomain}/agentforce/agentforce_mascot.png`} 
            alt="Agentforce Mascot" 
            className="w-8 h-8 object-contain"
          />
        </button>
      )}
    </div>
  );
};

export default AgentforceChat;
